import { useBulkSelection } from '@src/hooks/useBulkSelection';
import { useLocalPreviewVideoUrl } from '@src/hooks/useLocalPreviewVideoUrl';
import { clipBelongsToStory } from '@src/services/common/clipBelongsToStory';
import { clipDownloadJobs } from '@src/services/publish/clipDownloadJobs';
import {
  useDatoClient,
  useTemplatedPrompts,
  useTranscription,
  useUserIdentity,
} from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import CloseIcon from '@src/svgs/CloseIcon';
import FunnelIcon from '@src/svgs/FunnelIcon';
import ClipsCameraIcon from '@src/svgs/quick-actions/VideoIcon';
import SearchIconOutline from '@src/svgs/SearchIconOutline';
import { LogManager } from '@src/utility/Logger';
import { hasSpeakerData } from '@src/utility/speakerUtils';
import { isCompressedRenditionPending } from '@src/utility/storyReadiness';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';
import styled, { css } from 'styled-components';

import { useFlagsCombination } from '../../utility/useFlagsCombination';
import AIClipProducer from '../ClipProducer/AIClipProducer';
import {
  getLocalClipThumbnailFrame,
  useLocalClipProducerThumbnails,
} from '../ClipProducer/useLocalClipProducerThumbnails';
import { ConfirmModal } from '../common/ConfirmModal';
import { ProduceIcon } from '../common/icons';
import { ToggleGroup } from '../common/ToggleGroup';
import { TOOLTIP_STYLE } from '../common/tooltipStyles';
import { useOutsideAlerter } from '../transcript/useClickOutside';
import ClipBulkActionBar from './ClipBulkActionBar';
import ClipDeleteList from './ClipDeleteList';
import { isClipCombinable } from './clipFragmentsFromClips';
import {
  resolveClipPreviewSync,
  stripClipPreviewParams,
} from './clipPreviewUrlState';
import ClipProducerModalUI from './ClipProducer/ClipProducerModalUI';
import { isClipSelectable } from './clipSelection';
import { clipSelectionSummary } from './clipSelectionSummary';
import { clipsInPickOrder, selectionPositionsFor } from './clipsInPickOrder';
import { useClipAssignees } from './hooks/useClipAssignees';
import { useClipCommentCounts } from './hooks/useClipCommentCounts';
import { useStoryClipPublishRecords } from './hooks/useStoryClipPublishRecords';
import { useSectionSpeakers } from './ReelEditor/useSectionSpeakers';
import ReelProducerConfigModal from './ReelProducer/ReelProducerConfigModal';
import ReelProducerLayout from './ReelProducer/ReelProducerLayout';
import SocialClipsFilterDropdown from './SocialClipsFilterDropdown';
import SocialClipShareModal from './SocialClipShareModal';
import SocialClipShareModalOld from './SocialClipShareModalOld';
import { useDeleteClips } from './useDeleteClips';
import { useDownloadClips } from './useDownloadClips';
import { useOpenClipPreview } from './useOpenClipPreview';
import { MIN_CLIPS_FOR_REEL, useSaveClipsAsReel } from './useSaveClipsAsReel';
import { useSocialClipsFilter } from './useSocialClipsFilter';
import VideoClipCard, { CLIP_CARD_SPEAKER_TOOLTIP_ID } from './VideoClipCard';

import type { ClipRef } from '../../types.ts/comment';
import type { Story } from '../../types.ts/story';
import type { ClipBulkAction } from './ClipBulkActionBar';
import type { ReelProducerConfig } from './ReelProducer/types';
import type { FC } from 'react';

type Props = {
  aiGeneratedContent?: Story['aiGeneratedContent'];
};
const logger = LogManager.getLogger('SocialClipsView');
const SocialClipsView: FC<Props> = observer(() => {
  const videoCreator = useVideoCreatorStore();
  const userIdentity = useUserIdentity();
  const templatedPrompts = useTemplatedPrompts();
  const transcriptionStore = useTranscription();
  const datoClientStore = useDatoClient();

  // The story's `spk_X` labels resolved to people, once for the whole grid —
  // the cards' face piles read it rather than each resolving its own. The same
  // cached hook the Reel Editor's Section rows use, so a person is the same
  // face and the same colour in both places, and neither costs a second fetch.
  const speakersByLabel = useSectionSpeakers(
    videoCreator.story?.id,
    datoClientStore,
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const [showProducerModal, toggleProducerModal] = useState<boolean>(false);
  const [showReelModal, setShowReelModal] = useState(false);
  const [reelConfig, setReelConfig] = useState<ReelProducerConfig | null>(null);
  const [showReelProducer, setShowReelProducer] = useState(false);
  const [showFilterDropdown, setShowFilterDropdown] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState('');
  const filterDropdownRef = useRef<HTMLDivElement>(null);

  const clipProducerPresets = templatedPrompts?.clipProducerPresets;
  // PLAT-11445 (audio-first): clip and reel production is a transcript
  // action — clips are transcript ranges, previews resolve their media from
  // the story at open — so both producers gate on the transcript, not on the
  // video renditions. Gate on the story's ORIGINAL transcript (what the
  // producer reads): `isTranscriptionReady` also requires the editor-synced
  // element list, which is empty while the story master's mid-pending
  // composition cannot build a transcript track.
  const producersBlocked =
    (transcriptionStore.originalTranscription?.elements?.length ?? 0) === 0;
  const producersBlockedTitle = producersBlocked
    ? 'Transcript is still processing'
    : undefined;
  const {
    contentStudioEnableClipProducer,
    enableTranscriptEditor,
    enableContentStudioClipEditPreview,
    enableClipProducerReels,
    contentStudioModalEnableComments,
    contentStudioEnableClipStatusAssignee,
    enableBasicReelCreation,
    enableBulkClipDelete,
    enableTranslationReviewerMode,
  } = useFlagsCombination(userIdentity.currentRole);

  // Combining clips into a reel is authoring, so a translation reviewer gets
  // neither the card markers nor the bulk bar — the same call that hides the
  // Reel Editor subtab from them (PLAT-11035).
  const canCombineClips =
    enableBasicReelCreation && !enableTranslationReviewerMode;

  // Deleting is destructive rather than authoring, but the reviewer exclusion
  // is the same one (PLAT-11118).
  const canDeleteClips = enableBulkClipDelete && !enableTranslationReviewerMode;

  const canSelectClips = canCombineClips || canDeleteClips;

  const {
    selectedIds,
    toggleItem,
    clearSelection,
    selectOnly,
    pruneSelection,
    isSelected,
  } = useBulkSelection();
  const { saveAsReel, isSaving: isSavingReel } = useSaveClipsAsReel();
  const { deleteClips, isDeleting } = useDeleteClips();
  const { downloadClips, isDownloading } = useDownloadClips();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // The story's publish records, fetched once for the whole grid. A clip
  // published only in a translation records no legacy publish fields, so
  // without these the cards, the Published filter and the recency sort all read
  // a dubbed publish as a draft (PLAT-11591).
  const visibleStoryClips = videoCreator.story?.otherVideos?.filter(
    video => !video.isHidden,
  );
  const publishRecordsFor = useStoryClipPublishRecords(
    videoCreator.story?.id,
    visibleStoryClips,
  );
  const originalLanguage = videoCreator.story?.originalVideoLanguage || 'en';

  // Use the filter hook
  const {
    filters,
    filteredVideos,
    availableCreators,
    availableAssignees,
    activeFilterCount,
    toggleClipStatusFilter,
    toggleClipLengthFilter,
    toggleCreatorFilter,
    toggleReviewStatusFilter,
    toggleAssigneeFilter,
  } = useSocialClipsFilter(
    visibleStoryClips,
    publishRecordsFor,
    originalLanguage,
  );

  // Map assignee emails to showcase-member names for the filter labels.
  const assignees = useClipAssignees();
  const assigneeLabels = useMemo(() => {
    const labels: Record<string, string> = {};
    assignees.forEach(a => {
      if (a.name) labels[a.email] = a.name;
    });
    return labels;
  }, [assignees]);

  // Close dropdown when clicking outside
  useOutsideAlerter(filterDropdownRef, () => setShowFilterDropdown(false));

  // PLAT-11249 — which slice of the grid is showing. All is the default:
  // finding content (recency scans, a search whose kind you don't remember)
  // cuts across kinds, and the cards' identity badges keep a mixed grid
  // legible; Clips and Reels are the focused views for working in one kind.
  const [clipKind, setClipKind] = useState<'all' | 'clips' | 'reels'>('all');

  const visibleVideos = useMemo(() => {
    // The segment partitions first (PLAT-11249): the dropdown filters and the
    // search narrow within it. The same `isReel` rule the cards' badge reads.
    const inKind =
      clipKind === 'all'
        ? filteredVideos
        : filteredVideos.filter(v =>
            clipKind === 'reels' ? !!v.clipJson?.isReel : !v.clipJson?.isReel,
          );
    if (!searchQuery.trim()) return inKind;
    const q = searchQuery.toLowerCase();
    return inKind.filter(v => v.title?.toLowerCase().includes(q));
  }, [filteredVideos, searchQuery, clipKind]);
  const localPreviewUrl = useLocalPreviewVideoUrl(videoCreator.story?.id);
  const localCardRanges = useMemo(
    () =>
      visibleVideos
        .map(video => video.clipJson)
        .filter((clip): clip is NonNullable<typeof clip> => !!clip)
        .map(clip => ({
          startTime: clip.startTime,
          duration: clip.duration,
        })),
    [visibleVideos],
  );
  const preferLocalThumbnails =
    !!localPreviewUrl && isCompressedRenditionPending(videoCreator.story);
  const localCardPosters = useLocalClipProducerThumbnails(
    localPreviewUrl,
    localCardRanges,
    preferLocalThumbnails,
  );

  // Selection order is the reel's playback order (PLAT-11049, epic DE11), so
  // this resolves ids through `selectedIds` (a Set, insertion-ordered) rather
  // than by walking the grid. Clips filtered or searched out of view drop out,
  // so the bar's count never exceeds what is on screen.
  const selectedClips = useMemo(
    () => clipsInPickOrder(visibleVideos, selectedIds),
    [selectedIds, visibleVideos],
  );

  // The position each pick takes in the reel, shown in the cards' selection
  // markers (PLAT-11092). Derived from the clips above — the same list Save as
  // Reel is handed — so a number is never a position the reel will not have.
  const selectionPositions = useMemo(
    () => selectionPositionsFor(selectedClips),
    [selectedClips],
  );

  // A deleted clip leaves the selection with it (PLAT-11092): otherwise its id
  // sits on in the Set, invisible to the count and the numbers but ready to
  // rejoin the reel. Pruned against every clip in the story rather than the
  // grid, so a clip merely filtered or searched out of view keeps its pick.
  const storyClipIds = useMemo(() => {
    const clips = videoCreator.story?.otherVideos;
    if (!clips) return null;
    return new Set(
      clips
        .filter(clip => !clip.isHidden)
        .map(clip => clip.id)
        .filter((id): id is string => !!id),
    );
  }, [videoCreator.story?.otherVideos]);

  useEffect(() => {
    if (storyClipIds) pruneSelection(storyClipIds);
  }, [storyClipIds, pruneSelection]);

  // The click that lands on an empty selection is pick #1 of a new reel, so it
  // starts the selection over instead of adding to it. Without this, picks that
  // dropped out of view (deleted, filtered, or searched away) come back the
  // moment the grid shows them again and the reel is longer than the numbers
  // on the cards ever said (PLAT-11092).
  const handleToggleSelect = useCallback(
    (clipId: string) => {
      if (selectedClips.length === 0) {
        selectOnly(clipId);
        return;
      }
      toggleItem(clipId);
    },
    [selectedClips.length, selectOnly, toggleItem],
  );

  // Selection is wider than the reel it may become (PLAT-11118), so Save as
  // Reel has to account for picks it cannot use. Skipping them silently would
  // build a reel shorter than the numbers on the cards promised, so it disables
  // itself and says which picks are the problem.
  const uncombinableCount = useMemo(
    () => selectedClips.filter(clip => !isClipCombinable(clip)).length,
    [selectedClips],
  );

  // Files a Download would produce: every published aspect ratio of every pick,
  // from the legacy render or — for a clip published only in a translation —
  // from its publish record (PLAT-11660). Zero means nothing in the selection
  // has been published yet, and the action is then absent rather than
  // present-and-disabled — there is no file to hand over and nothing the
  // creator could do about it from the bar.
  //
  // The list itself is what `downloadClips` is handed, so the gate can never
  // offer a Download that then finds no file. Not memoized: `publishRecordsFor`
  // is rebuilt every render to stay reactive to records landing (see
  // `useStoryClipPublishRecords`), so a memo here would never hit.
  const downloadJobs = clipDownloadJobs(
    selectedClips,
    publishRecordsFor,
    originalLanguage,
  );

  // What the picks add up to, beside the bar's count.
  const selectionSummary = useMemo(
    () => clipSelectionSummary(selectedClips, speakersByLabel),
    [selectedClips, speakersByLabel],
  );

  const bulkActions = useMemo<ClipBulkAction[]>(() => {
    const actions: ClipBulkAction[] = [];

    if (canCombineClips) {
      const tooFewClips = selectedClips.length < MIN_CLIPS_FOR_REEL;
      actions.push({
        key: 'save-as-reel',
        label: isSavingReel ? 'Saving…' : 'Save as Reel',
        variant: 'primary',
        disabled:
          isSavingReel || isDeleting || tooFewClips || uncombinableCount > 0,
        // Only a reason the selection can act on. A transient in-flight
        // disable (saving, deleting) explains itself through the labels and
        // the emptying grid, and must not borrow a selection's reason — it
        // would read "0 selected clips can't be combined into a reel".
        disabledReason: tooFewClips
          ? `Select at least ${MIN_CLIPS_FOR_REEL} clips`
          : uncombinableCount > 0
            ? `${uncombinableCount} selected clip${
                uncombinableCount === 1 ? '' : 's'
              } can't be combined into a reel`
            : undefined,
        onClick: async () => {
          const saved = await saveAsReel(selectedClips);
          // On failure the selection stays put so the creator can retry
          // without re-picking what they just picked.
          if (saved) clearSelection();
        },
      });
    }

    // Between the reel action and Delete: a supporting action, and nowhere
    // near the destructive one.
    if (downloadJobs.length > 0) {
      actions.push({
        key: 'download',
        label: isDownloading ? 'Downloading…' : 'Download',
        variant: 'secondary',
        disabled: isDownloading || isDeleting,
        onClick: () => void downloadClips(downloadJobs),
      });
    }

    // Delete sits last: the destructive action is the furthest from the
    // selection it would act on.
    if (canDeleteClips) {
      actions.push({
        key: 'delete',
        label: isDeleting ? 'Deleting…' : 'Delete',
        variant: 'destructive',
        disabled: isDeleting || selectedClips.length === 0,
        onClick: () => setShowDeleteConfirm(true),
      });
    }

    return actions;
  }, [
    canDeleteClips,
    canCombineClips,
    downloadJobs,
    isDeleting,
    isDownloading,
    isSavingReel,
    selectedClips,
    uncombinableCount,
    downloadClips,
    saveAsReel,
    clearSelection,
  ]);

  // What the story holds, split by kind for the header. Counted off every clip
  // rather than the grid, so filtering or searching narrows the list without
  // rewriting the section's own label. `clipJson.isReel` is the same rule the
  // Type filter reads (PLAT-11128).
  const { clipCount, reelCount } = useMemo(() => {
    const clips =
      videoCreator.story?.otherVideos?.filter(video => !video.isHidden) ?? [];
    const reels = clips.filter(clip => clip.clipJson?.isReel).length;
    return { clipCount: clips.length - reels, reelCount: reels };
  }, [videoCreator.story?.otherVideos]);

  // Comment-count indicator: one batched fetch over the visible clips' Video
  // records (primary + associated). Skipped entirely when comments are off.
  const commentRefs = useMemo<ClipRef[]>(
    () =>
      contentStudioModalEnableComments
        ? visibleVideos
            .flatMap(v => [v.id, ...(v.associatedVideos ?? []).map(a => a.id)])
            .filter((id): id is string => !!id)
            .map(videoId => ({ videoId }))
        : [],
    [contentStudioModalEnableComments, visibleVideos],
  );
  const {
    getCount: getCommentCount,
    getThreadSummary: getCommentThreads,
    getLatest: getLatestComment,
    refresh: refreshCommentCounts,
    applyDelta: applyCommentCountDelta,
  } = useClipCommentCounts(commentRefs);

  // The list counts are fetched once and don't observe comments added inside
  // the clip-preview modal, so refetch when the modal closes (it may have
  // gained comments). Tracks the previous preview to fire only on close.
  const clipPreview = videoCreator.videoClipPreview;
  const prevClipPreviewRef = useRef(clipPreview);
  useEffect(() => {
    if (prevClipPreviewRef.current && !clipPreview) {
      refreshCommentCounts();
      // PLAT-11249 — reveal what was just worked on: if the current segment
      // would hide the closed item's card, switch to its kind. All and the
      // matching segment already show it, so they stand. This is what puts a
      // clip converted to a reel in view — the preview swapped onto the new
      // reel at conversion, so the kind read here is the reel's.
      const closedKind = prevClipPreviewRef.current.clipJson?.isReel
        ? 'reels'
        : 'clips';
      setClipKind(current => (current === 'all' ? current : closedKind));
    }
    prevClipPreviewRef.current = clipPreview;
  }, [clipPreview, refreshCommentCounts]);

  // PLAT-10948: the open clip is persisted in the URL (`?clipId=<variantId>`) so
  // the preview is shareable and survives refresh / Back-Forward. The value is
  // the selected aspect-ratio *variant* id, so a link reproduces the exact AR
  // the sender saw. The params are written by the modal (one consolidated write,
  // given react-router's stale-closure `setSearchParams`) and the review by the
  // bridge; this view only opens from `?clipId`, closes on Back, and strips them.
  const { open: openClipPreview } = useOpenClipPreview();
  const clipParam = searchParams.get('clipId');

  // URL -> store, both directions. The decision lives in
  // `resolveClipPreviewSync` so the transition table is unit-tested; `handledRef`
  // latches the `?clipId` value already acted on, which is what keeps unrelated
  // store / clip-list churn from re-opening a value already handled (that
  // replaces `videoClipPreview` and reloads the renderer mid-edit). It also
  // stands in for the old prev-param ref: a latch set only after acting proves
  // the param was present in an earlier render, so neither an initial load nor a
  // card-click open (store set before the modal writes the param) reads as a
  // Back-close.
  const handledClipParamRef = useRef<string | null>(null);
  useEffect(() => {
    const { action, handled, match } = resolveClipPreviewSync({
      clipParam,
      handled: handledClipParamRef.current,
      preview: clipPreview,
      otherVideos: videoCreator.story?.otherVideos,
    });
    handledClipParamRef.current = handled;
    if (action === 'open' && match) {
      void openClipPreview(match.owner, false, match.targetAspectRatio);
    } else if (action === 'close') {
      videoCreator.videoClipPreview = null;
    }
  }, [
    clipParam,
    clipPreview,
    videoCreator,
    videoCreator.story?.otherVideos,
    openClipPreview,
  ]);

  // Close the preview and strip its params in one shot so the X button can't be
  // immediately reopened by the sync effect (both land in the same render, so
  // `?clipId` is already gone when it re-runs). Also the close path for a shared
  // link — leaves a clean "list" URL. Clearing the latch lets the same clip be
  // reopened afterwards.
  const closeClipPreview = useCallback(() => {
    videoCreator.videoClipPreview = null;
    handledClipParamRef.current = null;
    setSearchParams(prev => stripClipPreviewParams(prev), { replace: true });
  }, [videoCreator, setSearchParams]);

  // Auto-open clip producer when navigating from the sidebar "Produce Clips" link
  useEffect(() => {
    if (
      searchParams.get('produce') === 'true' &&
      contentStudioEnableClipProducer &&
      clipProducerPresets?.length
    ) {
      videoCreator.videoClipPreview = null;
      toggleProducerModal(true);
      // Force-closing the preview has to take its params with it (PLAT-10948),
      // or `?clipId` sits stale in the URL behind the producer modal.
      const newParams = stripClipPreviewParams(searchParams);
      newParams.delete('produce');
      handledClipParamRef.current = null;
      setSearchParams(newParams, { replace: true });
    }
  }, [
    searchParams,
    setSearchParams,
    contentStudioEnableClipProducer,
    clipProducerPresets,
  ]);

  // Determine if speaker selection should be enabled based on:
  // 1. The transcript editor feature flag being enabled
  // 2. The presence of speaker data in the transcript
  const enableSpeakerSelection = useMemo(() => {
    if (!enableTranscriptEditor) return false;

    const transcriptElements =
      transcriptionStore.getFinalTranscriptionElements();
    return hasSpeakerData(transcriptElements);
  }, [
    enableTranscriptEditor,
    transcriptionStore.getFinalTranscriptionElements(),
  ]);

  useEffect(() => {
    const otherVideos = videoCreator.story?.otherVideos;
    if (!otherVideos) return;

    const idCounts = new Map<string, number>();

    for (const video of otherVideos) {
      if (video.isHidden) continue;

      if (!video.id) {
        logger.error(`no ID for video clip in story ${videoCreator.story?.id}`);
        continue;
      }

      idCounts.set(video.id, (idCounts.get(video.id) ?? 0) + 1);
    }

    const duplicates = Array.from(idCounts.entries()).filter(
      ([, count]) => count > 1,
    );
    if (duplicates.length > 0) {
      logger.error(
        `duplicated IDs for video clip in story ${videoCreator.story?.id}`,
        duplicates,
      );
    }
  }, [videoCreator.story?.otherVideos]);

  return (
    <Main data-testid="video-clips-section">
      <Header>
        {/* PLAT-11249 — the split the header used to only *say*, made the
            view: counted off the whole story so filtering or searching
            narrows the grid without rewriting the control. A segmented
            toggle, not a second row of tabs — the section tabs above already
            own that shape, and the design system's precedent for "a few
            mutually exclusive views of one list, All included" is the
            comments filter's ToggleGroup. */}
        <KindToggle>
          <ToggleGroup<'all' | 'clips' | 'reels'>
            options={[
              { id: 'all', label: 'All' },
              { id: 'clips', label: 'Clips', count: clipCount },
              { id: 'reels', label: 'Reels', count: reelCount },
            ]}
            value={clipKind}
            onChange={setClipKind}
            ariaLabel="Show all content, clips or reels"
            testIdPrefix="clips-view-tab"
          />
        </KindToggle>
        <RightGroup>
          <ClipCount $visible={activeFilterCount > 0 || !!searchQuery}>
            {visibleVideos.length}{' '}
            {clipKind === 'all'
              ? visibleVideos.length === 1
                ? 'item'
                : 'items'
              : clipKind === 'reels'
                ? visibleVideos.length === 1
                  ? 'reel'
                  : 'reels'
                : visibleVideos.length === 1
                  ? 'clip'
                  : 'clips'}
          </ClipCount>
          <SearchWrapper>
            <SearchIconOutline strokeColor="var(--text-muted)" />
            <SearchInput
              type="text"
              // Contextual to the tab (PLAT-11249): the search only ever
              // reaches what the active tab holds.
              placeholder={
                clipKind === 'all'
                  ? 'Search clips and reels…'
                  : clipKind === 'reels'
                    ? 'Search reels…'
                    : 'Search clips…'
              }
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <ClearButton
                aria-label="Clear search"
                onClick={() => setSearchQuery('')}
              >
                <CloseIcon width={10} height={10} />
              </ClearButton>
            )}
          </SearchWrapper>
          <ButtonGroup>
            <FilterButtonContainer ref={filterDropdownRef}>
              <FilterButton
                onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                data-testid="video-clips-filter-button"
              >
                <FunnelIcon strokeColor="currentColor" />
                <span>
                  Filter {activeFilterCount > 0 && `(${activeFilterCount})`}
                </span>
              </FilterButton>
              {showFilterDropdown && (
                <SocialClipsFilterDropdown
                  filters={filters}
                  availableCreators={availableCreators}
                  availableAssignees={availableAssignees}
                  assigneeLabels={assigneeLabels}
                  showReviewWorkflow={contentStudioEnableClipStatusAssignee}
                  kindNoun={clipKind === 'reels' ? 'Reel' : 'Clip'}
                  toggleClipStatusFilter={toggleClipStatusFilter}
                  toggleClipLengthFilter={toggleClipLengthFilter}
                  toggleCreatorFilter={toggleCreatorFilter}
                  toggleReviewStatusFilter={toggleReviewStatusFilter}
                  toggleAssigneeFilter={toggleAssigneeFilter}
                />
              )}
            </FilterButtonContainer>
            {/* Both produce actions stay on every segment: the toggle filters
                what you SEE, not what you can DO, so the pair holds its place
                and the active segment only picks which one reads as primary —
                All leads with Produce Clips, the grid's main production path.
                Exactly one primary per row (design-system.md); a lone button
                (its sibling's flag off) is always the primary. */}
            {contentStudioEnableClipProducer && (
              <ProducerButton
                $primary={clipKind !== 'reels' || !enableClipProducerReels}
                disabled={!clipProducerPresets?.length || producersBlocked}
                onClick={() => {
                  if (clipProducerPresets?.length && !producersBlocked) {
                    toggleProducerModal(true);
                  }
                }}
                data-testid="open-produce-clips"
                title={producersBlockedTitle}
              >
                <ClipsCameraIcon width="16" height="16" />{' '}
                <span>Produce Clips</span>
              </ProducerButton>
            )}
            {enableClipProducerReels && (
              <ProducerButton
                $primary={
                  clipKind === 'reels' || !contentStudioEnableClipProducer
                }
                disabled={producersBlocked}
                onClick={() => {
                  if (!producersBlocked) {
                    setShowReelModal(true);
                  }
                }}
                data-testid="open-produce-reel"
                title={producersBlockedTitle}
              >
                <ProduceIcon /> <span>Produce Reel</span>
              </ProducerButton>
            )}
          </ButtonGroup>
        </RightGroup>
      </Header>
      {videoCreator.videoClipPreview &&
        clipBelongsToStory(videoCreator.videoClipPreview, videoCreator.story) &&
        (enableContentStudioClipEditPreview ? (
          <SocialClipShareModal
            clip={videoCreator.videoClipPreview}
            onClose={closeClipPreview}
            onCommentCountChange={(videoId, delta) =>
              applyCommentCountDelta(videoId, delta)
            }
          />
        ) : (
          <SocialClipShareModalOld
            clip={videoCreator.videoClipPreview}
            onClose={closeClipPreview}
          />
        ))}
      {/* An empty tab says so rather than rendering a blank grid (PLAT-11249):
          a Reels tab with nothing in it otherwise reads as broken. */}
      {visibleVideos.length === 0 && (
        <EmptyTabStatus data-testid="clips-view-empty">
          {activeFilterCount > 0 || searchQuery.trim()
            ? `No ${
                clipKind === 'all'
                  ? 'clips or reels'
                  : clipKind === 'reels'
                    ? 'reels'
                    : 'clips'
              } match`
            : clipKind === 'reels'
              ? 'No reels yet — select clips and Save as Reel to create one'
              : 'No clips yet'}
        </EmptyTabStatus>
      )}
      <Content>
        {visibleVideos.map((video, index) => {
          const ids = [
            video.id,
            ...(video.associatedVideos ?? []).map(a => a.id),
          ].filter((id): id is string => !!id);
          return (
            <VideoClipCard
              key={video.id || index}
              video={video}
              localThumbnailUrl={
                video.clipJson
                  ? localCardPosters.get(
                      getLocalClipThumbnailFrame(video.clipJson),
                    )
                  : undefined
              }
              preferLocalThumbnail={preferLocalThumbnails}
              commentCount={getCommentCount(ids)}
              commentThreads={getCommentThreads(ids)}
              latestComment={getLatestComment(ids)}
              selectable={
                // Each flag only widens what it is responsible for: with
                // delete off this is exactly the combinable-only gate that
                // shipped with PLAT-11049.
                (canCombineClips && isClipCombinable(video)) ||
                (canDeleteClips && isClipSelectable(video))
              }
              selected={!!video.id && isSelected(video.id)}
              selectionOrder={
                video.id ? (selectionPositions.get(video.id) ?? null) : null
              }
              selectionActive={selectedIds.size > 0}
              onToggleSelect={handleToggleSelect}
              speakersByLabel={speakersByLabel}
              publishRecordsFor={publishRecordsFor}
            />
          );
        })}
      </Content>
      {/* One instance for the whole grid, the way `RecentContent` renders the
          shared one — a Tooltip per card would mount one per clip on screen. */}
      <Tooltip id={CLIP_CARD_SPEAKER_TOOLTIP_ID} style={TOOLTIP_STYLE} />
      {canSelectClips && (
        <ClipBulkActionBar
          count={selectedClips.length}
          summary={selectionSummary.duration}
          countTooltip={selectionSummary.detail}
          onClear={clearSelection}
          actions={bulkActions}
        />
      )}
      {showDeleteConfirm && (
        <ConfirmModal
          closeModal={() => setShowDeleteConfirm(false)}
          onCancel={() => setShowDeleteConfirm(false)}
          onConfirm={async () => {
            setShowDeleteConfirm(false);
            // The selection is not cleared here: pruning drops the deleted
            // clips on its own, which leaves any that failed still picked and
            // ready to retry (PLAT-11118).
            await deleteClips(selectedClips);
          }}
          title={`Delete ${selectedClips.length} clip${
            selectedClips.length === 1 ? '' : 's'
          }?`}
          message="This cannot be undone."
          details={<ClipDeleteList clips={selectedClips} />}
          confirmText={`Delete ${selectedClips.length} clip${
            selectedClips.length === 1 ? '' : 's'
          }`}
          cancelText="Cancel"
        />
      )}
      {enableClipProducerReels && showReelModal && (
        <ReelProducerConfigModal
          onSubmit={(config: ReelProducerConfig) => {
            setReelConfig(config);
            setShowReelModal(false);
            setShowReelProducer(true);
          }}
          onClose={() => setShowReelModal(false)}
        />
      )}
      {showReelProducer && reelConfig && (
        <ReelProducerLayout
          config={reelConfig}
          onClose={() => {
            setShowReelProducer(false);
            setReelConfig(null);
          }}
        />
      )}
      {contentStudioEnableClipProducer && showProducerModal && (
        <AIClipProducer
          useFinalOrOriginalVideo={true}
          supportedPresets={templatedPrompts?.clipProducerPresets || null}
          enableSpeakerSelection={enableSpeakerSelection}
          ClipProducerUI={props => (
            <ClipProducerModalUI
              {...props}
              closeModal={() => {
                toggleProducerModal(false);
                if (searchParams.has('produce')) {
                  const next = new URLSearchParams(searchParams);
                  next.delete('produce');
                  setSearchParams(next, { replace: true });
                }
              }}
            />
          )}
        />
      )}
    </Main>
  );
});

const Main = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
`;

/* What an empty segment says instead of a blank grid (PLAT-11249). */
const EmptyTabStatus = styled.p`
  color: var(--text-muted);
  font-size: var(--text-sm);
  line-height: var(--leading-text-sm);
  margin: 0;
`;

/**
 * Sizes the kind toggle to the search field beside it: the two are the header
 * row's only inputs, and a control two-thirds the height of its neighbour reads
 * as belonging to a different row.
 *
 * The shared `ToggleGroup` keeps its own compact default — it is the same
 * control in dialogs and side panels, where it sits among smaller things — so
 * the override is on this instance, through the `className` the group accepts.
 * The arithmetic: the field is a 20px line box in 8px of padding inside a 1px
 * border (38px). The track spends 1px + 2px of its own, leaving 32px for a
 * segment, which is that same 20px line in 6px of padding.
 */
const KindToggle = styled.div`
  display: inline-flex;

  button {
    padding: 6px 12px;
    font-size: var(--text-sm);
    line-height: var(--leading-text-sm);
  }
`;

const SearchWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 280px;
  padding: 8px 12px;
  background-color: var(--input-background);
  border: 1px solid var(--input-border);
  border-radius: var(--radius-input);
  transition: border-color var(--transition-fast);

  &:hover {
    border-color: var(--text-muted);
  }

  &:focus-within {
    border-color: var(--accent);
  }
`;

const SearchInput = styled.input`
  flex: 1;
  background: none;
  border: none;
  outline: none;
  color: var(--text-secondary);
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-normal);

  &::placeholder {
    color: var(--text-muted);
  }
`;

const ClearButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: var(--text-muted);
  display: flex;
  align-items: center;

  &:hover {
    color: var(--text-secondary);
  }
`;

const RightGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ClipCount = styled.p<{ $visible?: boolean }>`
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-normal);
  line-height: normal;
  color: var(--text-muted);
  visibility: ${props => (props.$visible ? 'visible' : 'hidden')};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const FilterButtonContainer = styled.div`
  position: relative;
`;

const FilterButton = styled.button`
  display: flex;
  padding: 8px 12px;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border-radius: var(--radius-input);
  border: 1px solid var(--input-border);
  background: var(--input-background);
  cursor: pointer;
  color: var(--text-secondary);
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-medium);
  transition: all var(--transition-fast) ease;

  &:hover {
    border-color: var(--text-muted);
    color: var(--foreground);
  }
`;
const Content = styled.div`
  margin-top: 10px;
  row-gap: 40px;
  column-gap: 24px;
  place-items: center;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  padding-bottom: 30px;
`;

const EditButton = styled.button.attrs((props: { disabled: boolean }) => props)`
  display: flex;
  height: 48px;
  padding: 16px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  flex: 1 0 0;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: none;

  opacity: ${props => (props.disabled ? '0.5' : '1')};
  cursor: ${props => (props.disabled ? 'wait' : 'pointer')};

  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 700;
  line-height: normal;
`;

// data-variant mirrors the emphasis into the DOM for the segment tests.
const ProducerButton = styled(EditButton).attrs<{ $primary: boolean }>(
  ({ $primary }) => ({
    'data-variant': $primary ? 'primary' : 'secondary',
  }),
)<{ $primary: boolean }>`
  width: auto;
  min-width: 160px;
  height: 40px;

  ${({ $primary }) =>
    $primary
      ? css`
          background: var(--primary);
          color: var(--background);
          border: 1px solid var(--primary);
        `
      : css`
          background: transparent;
          color: var(--foreground);
          border: 1px solid var(--border);
          font-weight: var(--font-weight-medium);

          &:hover:not(:disabled) {
            background: var(--surface-elevated);
            border-color: var(--text-muted);
          }
        `}
`;

export default SocialClipsView;
